<template>
  <div class="">
    <v-card flat class="mb-4">
      <v-card-text class="pb-2 px-0">
        <v-row>
          <v-col md="3">
            <v-text-field
              hide-details="auto"
              type="date"
              :dense="false"
              solo
              flat
              v-model="searchParams.date_start"
              background-color="grey lighten-4"
              label="Data Inicial"
            />
          </v-col>
          <v-col md="3">
            <v-text-field
              background-color="grey lighten-4"
              hide-details="auto"
              type="date"
              solo
              :dense="false"
              flat
              v-model="searchParams.date_end"
              label="Data Final"
            />
          </v-col>

          <v-col md="2">
            <div class="fill-height d-flex align-center">
              <v-btn
                @click="select(1)"
                class="py-5"
                color="primary"
                elevation="0"
              >
                Pesquisar
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <v-row>
          <v-col md="4">
            <AccountMultiSelect
              v-model="searchParams.account_id"
              background-color="grey lighten-4"
              hide-details="auto"
              flat
              solo
              :dense="false"
              multiple
              :item-value="(val) => val.id"
              clearable
              @change="select(1)"
            />
          </v-col>

          <v-col md="4">
            <AccountPlanSelect
              v-model="searchParams.account_plan_id"
              background-color="grey lighten-4"
              hide-details="auto"
              :item-value="(val) => val.id"
              :dense="false"
              flat
              solo
              multiple
              clearable
              @change="select(1)"
            />
          </v-col>

          <v-col md="4">
            <v-select
              v-model="searchParams.group_by"
              background-color="grey lighten-4"
              hide-details="auto"
              :dense="false"
              flat
              solo
              multiple
              chips
              small-chips
              clearable
              label="Agrupar por"
              :items="groupByOptions"
              @change="select(1)"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="6">
            <v-select
              v-model="searchParams.origin"
              background-color="grey lighten-4"
              hide-details="auto"
              :dense="false"
              flat
              solo
              multiple
              chips
              small-chips
              clearable
              label="Filtrar por Origem"
              :items="originOptions"
              @change="select(1)"
            />
          </v-col>
          <v-col md="6">
            <v-select
              v-model="searchParams.payment_method"
              background-color="grey lighten-4"
              hide-details="auto"
              :dense="false"
              flat
              solo
              multiple
              chips
              small-chips
              clearable
              label="Filtrar por Método de Pagamento"
              :items="paymentMethodOptions"
              @change="select(1)"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-row>
      <v-col md="4">
        <v-card
          outlined
          color="grey lighten-4"
          elevation="0"
          class="fill-height"
        >
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Total de Transações
              <br />
              <b class="text-h4">
                {{ totals.total_transactions || 0 }}
              </b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col md="4">
        <v-card outlined color="primary" dark elevation="0" class="fill-height">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Valor Total
              <br />
              <b class="text-h4">
                {{ $format.moneyBr(totals.total_value || 0) }}
              </b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4 mt-4">
      <v-col>
        <v-card flat color="grey lighten-5">
          <v-card-text class="py-4 px-5">
            <v-row class="align-center">
              <v-col md="8">
                <div class="d-flex align-center flex-wrap">
                  <span
                    class="text-body-1 font-weight-medium mr-4 text-primary"
                  >
                    Ordenar por:
                  </span>
                  <v-chip-group
                    v-model="searchParams.order_by"
                    :value="validOrderBy"
                    mandatory
                    active-class="primary white--text"
                    @change="select(1)"
                  >
                    <v-chip
                      v-for="option in orderByOptions"
                      :key="option.value"
                      filter
                      :value="option.value"
                      class="mx-1"
                      small
                    >
                      {{ option.text }}
                    </v-chip>
                  </v-chip-group>
                </div>
              </v-col>
              <v-col md="4">
                <div class="d-flex align-center justify-end flex-wrap">
                  <span
                    class="text-body-1 font-weight-medium mr-3 text-primary"
                  >
                    Ordem:
                  </span>
                  <v-chip-group
                    v-model="searchParams.order_direction"
                    mandatory
                    active-class="primary white--text"
                    @change="select(1)"
                    class="mr-4"
                  >
                    <v-chip filter value="asc" class="mx-1" small>
                      Crescente
                    </v-chip>
                    <v-chip filter value="desc" class="mx-1" small>
                      Decrescente
                    </v-chip>
                  </v-chip-group>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-4">
      <v-col>
        <v-data-table
          flat
          disable-sort
          disable-filtering
          :headers="headers"
          hide-default-footer
          :items-per-page="-1"
          :loading="isLoading"
          class="table-striped grey lighten-4"
          :items="transactionReport.report ? transactionReport.report.data : []"
        >
          <template v-slot:item.payment_method="{ item }">
            {{
              paymentMethodLabels[item.payment_method] || item.payment_method
            }}
          </template>

          <template v-slot:item.origin="{ item }">
            {{ originLabels[item.origin] || item.origin }}
          </template>

          <template v-slot:item.account_plan_name="{ item }">
            {{ item.account_plan_name || "-" }}
          </template>

          <template v-slot:item.total_transactions="{ item }">
            {{ item.total_transactions }}
          </template>

          <template v-slot:item.total_value="{ item }">
            <span
              :class="
                parseFloat(item.total_value) < 0
                  ? 'error--text'
                  : 'success--text'
              "
            >
              {{ $format.moneyBr(item.total_value) }}
            </span>
          </template>
        </v-data-table>
        <app-pagination
          v-if="transactionReport.report"
          @click="select($event)"
          :data="transactionReport.report"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import AccountPlanSelect from "@/components/account-plan/ui/AccountPlanSelect.vue";
import AccountMultiSelect from "@/components/account/ui/AccountMultiSelect.vue";

import { format, startOfMonth } from "date-fns";

export default {
  components: {
    AccountPlanSelect,
    AccountMultiSelect,
  },

  data() {
    return {
      searchParams: {
        account_id: null,
        account_plan_id: [1],
        date_start: format(startOfMonth(new Date()), "yyyy-MM-dd"),
        date_end: format(new Date(), "yyyy-MM-dd"),
        order_by: "account_plan_name",
        order_direction: "asc",
        origin: ["sale", "custom", "bill_receivable_group"],
        group_by: [],
        payment_method: ["cash", "credit_card", "debit_card"],
        page: 1,
      },

      groupByOptions: [
        { text: "Origem", value: "origin" },
        { text: "Plano de Conta", value: "account_plan_id" },
      ],

      originOptions: [
        { text: "Venda", value: "sale" },
        { text: "Conta a Receber", value: "bill_receivable" },
        { text: "Grupo de Contas a Receber", value: "bill_receivable_group" },
        { text: "Crédito de Conta a Receber", value: "bill_receivable_credit" },
        { text: "Conta a Pagar", value: "bill_payable" },
        { text: "Personalizado", value: "custom" },
        { text: "Transferência", value: "transfer" },
      ],

      paymentMethodOptions: [
        { text: "Dinheiro", value: "cash" },
        { text: "Cartão de Crédito", value: "credit_card" },
        { text: "Cartão de Débito", value: "debit_card" },
        { text: "Cheque", value: "check" },
        { text: "Boleto", value: "bill" },
      ],

      columnsConfig: [
        {
          text: "Método de Pagamento",
          value: "payment_method",
          orderValue: "payment_method",
          align: "start",
          alwaysVisible: true,
        },
        {
          text: "Conta",
          value: "account_name",
          orderValue: "account_name",
          align: "start",
          alwaysVisible: true,
        },
        {
          text: "Origem",
          value: "origin",
          orderValue: "origin",
          align: "start",
          requiresGroupBy: "origin",
        },
        {
          text: "Plano de Conta",
          value: "account_plan_name",
          orderValue: "account_plan_name",
          align: "start",
          requiresGroupBy: "account_plan_id",
        },
        {
          text: "Total de Transações",
          value: "total_transactions",
          orderValue: "total_transactions",
          align: "center",
          alwaysVisible: true,
        },
        {
          text: "Valor Total",
          value: "total_value",
          orderValue: "total_value",
          align: "end",
          alwaysVisible: true,
        },
      ],

      transactionReport: {},

      totals: {
        total_transactions: 0,
        total_value: 0,
      },

      isLoading: false,

      paymentMethodLabels: {
        cash: "Dinheiro",
        credit_card: "Cartão de Crédito",
        debit_card: "Cartão de Débito",
        check: "Cheque",
        bill: "Boleto",
      },

      originLabels: {
        sale: "Venda",
        bill_receivable: "Conta a Receber",
        bill_receivable_group: "Grupo de Contas a Receber",
        bill_receivable_credit: "Crédito de Conta a Receber",
        bill_payable: "Conta a Pagar",
        custom: "Personalizado",
        transfer: "Transferência",
      },
    };
  },

  mounted() {
    this.select(1);
  },

  computed: {
    validOrderBy() {
      const currentOrderIsValid = this.orderByOptions.some(
        (option) => option.value === this.searchParams.order_by
      );

      return currentOrderIsValid && this.orderByOptions.length > 0
        ? this.searchParams.order_by
        : this.orderByOptions[0]?.value || "payment_method";
    },

    headers() {
      return this.columnsConfig
        .filter((col) => this.isColumnVisible(col))
        .map((col) => ({
          text: col.text,
          value: col.value,
          align: col.align,
          sortable: false,
        }));
    },

    orderByOptions() {
      return this.columnsConfig
        .filter((col) => this.isColumnVisible(col) && col.orderValue)
        .map((col) => ({
          text: col.text,
          value: col.orderValue,
        }));
    },
  },

  methods: {
    isColumnVisible(column) {
      if (column.alwaysVisible) {
        return true;
      }

      if (column.requiresGroupBy) {
        return this.searchParams.group_by.includes(column.requiresGroupBy);
      }

      return true;
    },
    async select(page = 1) {
      this.searchParams.page = page;

      try {
        this.isLoading = true;

        const params = {
          ...this.searchParams,
          order_by: this.validOrderBy,
        };

        const response = await this.$http.index(
          "account/acc-transaction-payment-method",
          params
        );

        this.transactionReport = response;

        this.totals = response.totals || {
          total_transactions: 0,
          total_value: 0,
        };
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
